import { ROCKET_LEAGUE_LEADERBOARD_TYPES } from '../../../../mixins/games.commons';
import * as ProfileHelpers from './ProfileHelpers';
import dayjs from '../../../../utils/dayjs';

export const ROCKET_LEAGUE_STATS_NAME = {
  goalsPerGame: 'Goals per game',
  savesPerGame: 'Saves per game',
  assistsPerGame: 'Assists Per Game',
  wins: 'Wins',
  losses: 'Losses',
  won: 'Won',
  loss: 'Loss',
  rank: 'Rank',
  rankName: 'Rank',
  previousRankDiff: 'Previous rank Diff',

  // Leaderboard
  [ROCKET_LEAGUE_LEADERBOARD_TYPES.RATING]: 'Rating',
  [ROCKET_LEAGUE_LEADERBOARD_TYPES.SAVES]: 'Saves',
  [ROCKET_LEAGUE_LEADERBOARD_TYPES.GOALS]: 'Goals',
  [ROCKET_LEAGUE_LEADERBOARD_TYPES.ASSISTS]: 'Assists',
};

export const ROCKET_LEAGUE_STATS_NAME_ABBR = {
};

export const ROCKET_LEAGUE_CORE_STATS_ORDER = [
  'goalsPerGame',
  'savesPerGame',
  'assistsPerGame',
];

export const ROCKET_LEAGUE_MONTHLY_STATS_TOOLTIP_ORDER = [
  'won',
  'loss',
];

export const ROCKET_LEAGUE_QUEUES_STATS_ORDER = [
  'rankName',
  'previousRankDiff',
];

export const RANKS_TO_SHOW = [
  'hub',
  'state',
  'country',
];

export const ROCKET_LEAGUE_STATS_TYPES = {
  mvpWinShare: 'percent',
};

export const ROCKET_LEAGUE_QUEUE_TYPES = {
  '1v1': '1v1',
  '2v2': '2v2',
  '3v3': '3v3',
  '3v3-solo': '3v3 s',
};

export const ROCKET_LEAGUE_STATS_INVERTED = {
};

export const rocketLeagueRankScaleFactor = {
  'BRONZE_I': 0,
  'BRONZE_II': 100,
  'BRONZE_III': 200,
  'SILVER_I': 300,
  'SILVER_II': 400,
  'SILVER_III': 500,
  'GOLD_I': 600,
  'GOLD_II': 700,
  'GOLD_III': 800,
  'PLATINUM_I': 900,
  'PLATINUM_II': 1000,
  'PLATINUM_III': 1100,
  'DIAMOND_I': 1200,
  'DIAMOND_II': 1300,
  'DIAMOND_III': 1400,
  'CHAMPION_I': 1500,
  'CHAMPION_II': 1600,
  'CHAMPION_III': 1700,
  'GRAND CHAMPION_I': 1800,
  'GRAND CHAMPION_II': 1900,
  'GRAND CHAMPION_III': 2000,
  'SUPERSONIC LEGEND': 2100,
};
export const rocketLeagueDivisionScaleFactor = {
  'I': 20,
  'II': 40,
  'III': 60,
  'IV': 80,
};

export function getFeaturedStatisticsData(gameStats, profileGeneralDatal) {
  const tier = gameStats.general.tier;
  const rank = gameStats.general.rank;
  const division = gameStats.general.division;
  return {
    avatar: profileGeneralDatal.avatar,
    name: gameStats.general.playerName || 'unknown',
    rankAvatar: getRocketLeagueRankAvatar(tier, rank), // Using RL rank
    rank: {
      name: getRankName(tier, rank, division), // ex: Gold II Division I or UNRANKED
      info: ROCKET_LEAGUE_QUEUE_TYPES[gameStats.general.rankQueue],
    },
  };
}

export function getOverviewStats(gameData) {
  return {
    rating: gameData.general.rating,
    ratingPercentile: gameData.general.ratingPercentile,
  };
}

export function getCoreStats(gameData) {
  const stats = [];
  let maxGraphValue = 0;

  ROCKET_LEAGUE_CORE_STATS_ORDER.forEach(statName => {
    stats.push({
      name: ROCKET_LEAGUE_STATS_NAME[statName],
      playerValue: gameData.general[statName],
      othersValue: gameData.general.others[statName],
      symbol: ProfileHelpers.parseStatSymbol(statName, ROCKET_LEAGUE_STATS_TYPES),
    });

    maxGraphValue = gameData.general[statName] > maxGraphValue ? gameData.general[statName] : maxGraphValue;
    maxGraphValue = gameData.general.others[statName] > maxGraphValue ? gameData.general.others[statName] : maxGraphValue;
  });

  return { maxGraphValue, stats };
}

export function getCoreStatsDiff(current, opponent) {
  const currentGoalsPerGame = current.stats.find(stat => stat.name === ROCKET_LEAGUE_STATS_NAME.goalsPerGame).playerValue;
  const opponentGoalsPerGame = opponent.stats.find(stat => stat.name === ROCKET_LEAGUE_STATS_NAME.goalsPerGame).playerValue;
  const currentSavesPerGame = current.stats.find(stat => stat.name === ROCKET_LEAGUE_STATS_NAME.savesPerGame).playerValue;
  const opponentSavesPerGame = opponent.stats.find(stat => stat.name === ROCKET_LEAGUE_STATS_NAME.savesPerGame).playerValue;
  const currentAssistsPerGame = current.stats.find(stat => stat.name === ROCKET_LEAGUE_STATS_NAME.assistsPerGame).playerValue;
  const opponentAssistsPerGame = opponent.stats.find(stat => stat.name === ROCKET_LEAGUE_STATS_NAME.assistsPerGame).playerValue;

  const diffGoalsPerGame = ProfileHelpers.calculateDiffPercent(currentGoalsPerGame, opponentGoalsPerGame);
  const diffSavesPerGame = ProfileHelpers.calculateDiffPercent(currentSavesPerGame, opponentSavesPerGame);
  const diffAssistsPerGame = ProfileHelpers.calculateDiffPercent(currentAssistsPerGame, opponentAssistsPerGame);

  // Christian Equation (rounded to 2 decimal places)
  const total = ((diffGoalsPerGame + diffSavesPerGame + diffAssistsPerGame) / 3);
  return total.toFixed();
}

export function getMonthlyStats(gameData) {
  let monthAvgPerformance = 0;
  let overallPerformance = gameData.general.avgPerformance;
  overallPerformance = overallPerformance > 0 ? overallPerformance : 1;

  // We add the variable won and loss to each day.
  const dailyActivity = gameData.activity.dailyActivity.concat().map(day => {
    const won = day.matches ? Math.round((day.winRatio * day.matches) / 100) : 0;
    const lossRatio = 100 - day.winRatio;
    const loss = day.matches ? Math.round((lossRatio * day.matches) / 100) : 0;
    return {
      ...day,
      won,
      loss,
    };
  });

  dailyActivity.forEach((day, i) => {
    monthAvgPerformance += day.avgPerformance;

    const stats = ROCKET_LEAGUE_MONTHLY_STATS_TOOLTIP_ORDER.map(statName => {
      return {
        name: ROCKET_LEAGUE_STATS_NAME[statName],
        value: day[statName],
        symbol: ProfileHelpers.parseStatSymbol(statName, ROCKET_LEAGUE_STATS_TYPES),
      };
    });

    // Note: We add performancePercentage to day data.
    dailyActivity[i] = {
      ...day,
      stats,
      performancePercentage: day.matches ? ((day.avgPerformance * 100) / overallPerformance).toFixed() : 0,
    };
  });

  // Note: We add the performancePercentage to month data.
  monthAvgPerformance = monthAvgPerformance / gameData.activity.dailyActivity.length;
  monthAvgPerformance = ((monthAvgPerformance * 100) / overallPerformance).toFixed();

  return {
    days: dailyActivity,
    week: gameData.activity.weekActivity,
    month: {
      ...gameData.activity.monthActivity,
      performancePercentage: monthAvgPerformance,
    },
  };
}

export function getQueuesStats(queuesMap) {
  const queues = [];
  for (let [queueName, queueStats] of Object.entries(queuesMap)) {
    queueName = queueName.toString(); // Need it to avoid error when runnin prod.
    const stats = [];
    const currentTier = queueStats.tier;
    const currentRank = queueStats.rank;
    const currentDivision = queueStats.division;

    const lastSeasonTier = queueStats.lastSeason.tier;
    const lastSeasonRank = queueStats.lastSeason.rank;
    const lastSeasonDivision = queueStats.lastSeason.division;

    const currentDivisionScore = rocketLeagueDivisionScaleFactor[currentDivision];
    const rankName = currentTier.toUpperCase() !== 'UNRANKED' ? `${currentTier} ${currentRank} Division ${currentDivision}` : 'Unranked';
    const currentRankScore = calculateOwnScore(0, currentTier, currentRank); // Calculate just the rank score value.
    const currentScore = calculateOwnScore(currentDivisionScore, currentTier, currentRank); // Calculate the overall rank + division score.

    const lastSeasonDivisionScore = rocketLeagueDivisionScaleFactor[lastSeasonDivision];
    const lastSeasonRankScore = calculateOwnScore(0, lastSeasonTier, lastSeasonRank); // Calculate just the rank score value.
    const lastSeasonScore = calculateOwnScore(lastSeasonDivisionScore, lastSeasonTier, lastSeasonRank); // Calculate the overall rank + division score.

    const currentAmountOfDivisions = (currentScore / 20) - (currentRankScore / 100); // Calculate the total amount of division the player has from bronce I Div I, to current rank.
    const lastSeasonAmountOfDivisions = (lastSeasonScore / 20) - (lastSeasonRankScore / 100); // Calculate the total amount of division the player has from Bronce I Div I, to current rank.

    let previousRankDiff = Math.floor(currentAmountOfDivisions - lastSeasonAmountOfDivisions);
    previousRankDiff = getValueWithSign(previousRankDiff);

    // We add rankName and previousRankDiff to queueStats before creating the property stats.
    queueStats = { ...queueStats, rankName, previousRankDiff };

    ROCKET_LEAGUE_QUEUES_STATS_ORDER.forEach(statName => {
      stats.push({
        name: ROCKET_LEAGUE_STATS_NAME[statName],
        nameAbbr: ROCKET_LEAGUE_STATS_NAME_ABBR[statName] || ROCKET_LEAGUE_STATS_NAME[statName],
        value: queueStats[statName],
        inverted: ROCKET_LEAGUE_STATS_INVERTED[statName],
        symbol: ProfileHelpers.parseStatSymbol(statName, ROCKET_LEAGUE_STATS_TYPES),
      });
    });

    queues.push({
      ...queueStats,
      icon: getRocketLeagueRankAvatar(queueStats.tier, queueStats.rank),
      customIconUrl: '/assets/ranks',
      type: ROCKET_LEAGUE_QUEUE_TYPES[queueName],
      disableDiff: true,
      stats,
    });
  }

  return queues;
}

export function getRankStats(gameStats, generalData) {
  const stats = [];
  RANKS_TO_SHOW.forEach(rankName => {
    if (!gameStats.rank[rankName]) {
      return;
    }

    if (rankName === 'hub') {
      if (!generalData.hubMembership[0]) {
        return;
      }
      const selectedHubData = generalData.hubMembership[0];
      // When select hub feature done in backend.
      // const selectedHubData = generalData.hubMembership.find(hub => hub.id === gameStats.rank.selectedHub);
      if (!selectedHubData) {
        return;
      }

      stats.push({ rank: `#${gameStats.rank[rankName]} rank`, location: selectedHubData.name, icon: rankName });
      return;
    }

    stats.push({ rank: `#${gameStats.rank[rankName]} rank`, location: generalData[rankName], icon: rankName });
  });

  return {
    playerName: gameStats.general.playerName,
    rankAvatar: getRocketLeagueRankAvatar(gameStats.general.tier, gameStats.general.rank), // Using RL rank
    rank: {
      name: getRankName(gameStats.general.tier, gameStats.general.rank, gameStats.general.division), // ex: Platinum I Division I or UNRANKED
      info: ROCKET_LEAGUE_QUEUE_TYPES[gameStats.general.rankQueue],
    },
    stats,
  };
}

export function getRankProgression(gameData) {
  const points = [];
  const times = [];
  const tooltips = [];
  const gridLabelImagesMap = generateRankImagesMap();
  gameData.rankProgression.forEach(rankData => {
    const hasRank = rankData.tier.toUpperCase() !== 'UNRANKED';
    const rankNameWithoutDivision = hasRank ? `${rankData.tier} ${rankData.rank}` : 'Unranked';
    const divisionScore = rocketLeagueDivisionScaleFactor[rankData.division];
    points.push(calculateOwnScore(divisionScore, rankData.tier, rankData.rank));
    tooltips.push(rankNameWithoutDivision);
    // NOTE with .utc() it will take +1 day in negative Z zones (like argentina -3)
    times.push(dayjs.unix(rankData.date).toISOString());
  });

  return { points, times, gridLabelImagesMap, tooltips };
}

export function getleaderboardListWithStatName(leaderboardList) {
  return leaderboardList.map(player => {
    return { ...player, statName: ROCKET_LEAGUE_STATS_NAME[player.leaderboardType] };
  });
}

function getRocketLeagueRankAvatar(tier, rank) {
  let rankAvatar = 'rocket-league-20210916/';
  const rankNumber = parseRocketLeagueRomanRanks(rank);

  // NOTE If we have rank we display an rankAvatar like `platinum_3.png`
  // if we DON'T have rank, we just display `platinum_${rank}.png` or `unranked.png`
  if (tier && tier.toUpperCase() !== 'UNRANKED') {
    rankAvatar = rankAvatar + tier.toLowerCase();
    if (rankNumber) {
      rankAvatar = rankAvatar + '_' + rankNumber;
    }
  } else {
    rankAvatar = rankAvatar + 'unranked';
  }

  return rankAvatar;
}

function parseRocketLeagueRomanRanks(romanRocketLeagueRank) {
  if (!romanRocketLeagueRank) {
    return NaN;
  }
  switch (romanRocketLeagueRank.toUpperCase()) {
    case 'I':
      return '1';
    case 'II':
      return '2';
    case 'III':
      return '3';

    default:
      return romanRocketLeagueRank;
  }
}

function calculateOwnScore(score, tier, rank) {
  if (tier.toUpperCase() === 'UNRANKED') {
    return 0;
  }
  let key;
  if (tier.toUpperCase() === 'SUPERSONIC LEGEND') {
    key = tier.toUpperCase();
  } else {
    key = (`${tier}_${rank}`).toUpperCase();
  }
  return rocketLeagueRankScaleFactor[key] + score;
}

function getRankName(tier, rank, division) {
  const isRanked = tier.toUpperCase() !== 'UNRANKED';
  // ex: Gold I Division I or UNRANKED
  return isRanked ? `${tier} ${rank} Division ${division}` : 'Unranked';
}

function generateRankImagesMap() {
  const rankMap = {};
  Object.keys(rocketLeagueRankScaleFactor).forEach(rankName => {
    const score = rocketLeagueRankScaleFactor[rankName];
    rankMap[score] = '/assets/ranks/rocket-league-20210916/' + humanizeRank(rankName);
  });

  return rankMap;
}

function humanizeRank(rankString) {
  if (rankString === 'SUPERSONIC LEGEND') {
    return rankString.toLowerCase();
  }
  const rankArray = rankString.split('_');
  const rank = rankArray[0].toLowerCase();
  const tier = parseRocketLeagueRomanRanks(rankArray[1]);

  return `${rank}_${tier}`;
}

function getValueWithSign(value) {
  const sign = Math.sign(value);
  switch (sign) {
    case 1:
      return `+${value}`;

    default:
      return `${value}`;
  }
}
